// Event time helpers.
//
// Events are stored as a naive wall-clock in the CREATOR's timezone:
//   date       "2026-07-14"   (YYYY-MM-DD)
//   start_time "18:00"        (24h HH:MM, local to `timezone`)
//   end_time   "18:15"
//   timezone   "America/New_York" (IANA)
//
// These helpers turn that into an absolute instant and format it in the
// VIEWER's local timezone, so a 6:00 PM ET show reads "3:00 PM PDT" for a
// viewer in California, "11:00 PM BST" in London, etc.

// Convert a wall-clock time in a given IANA timezone to an absolute Date.
export function zonedWallClockToDate(dateStr, timeStr, timeZone) {
  if (!dateStr) return null;
  const [y, m, d] = dateStr.split("-").map(Number);
  let hh = 0, mm = 0;
  if (timeStr && timeStr.includes(":")) {
    // Accept "18:00" or "6:00 PM"
    const pm = /pm/i.test(timeStr);
    const am = /am/i.test(timeStr);
    const [rawH, rawM] = timeStr.replace(/[^\d:]/g, "").split(":");
    hh = Number(rawH);
    mm = Number(rawM || 0);
    if (pm && hh < 12) hh += 12;
    if (am && hh === 12) hh = 0;
  }
  if (!y || !m || !d) return null;

  const tz = timeZone || "UTC";
  // Interpret (y,m,d,hh,mm) as if UTC, then correct by the tz's offset at
  // that moment so the resulting instant maps back to the intended wall clock.
  const asUTC = Date.UTC(y, m - 1, d, hh, mm);
  let offset = 0;
  try {
    const dtf = new Intl.DateTimeFormat("en-US", {
      timeZone: tz, hour12: false,
      year: "numeric", month: "2-digit", day: "2-digit",
      hour: "2-digit", minute: "2-digit",
    });
    const parts = {};
    dtf.formatToParts(new Date(asUTC)).forEach((p) => (parts[p.type] = p.value));
    const backUTC = Date.UTC(
      Number(parts.year), Number(parts.month) - 1, Number(parts.day),
      parts.hour === "24" ? 0 : Number(parts.hour), Number(parts.minute)
    );
    offset = backUTC - asUTC; // ms the tz is ahead of UTC at that instant
  } catch {
    offset = 0;
  }
  return new Date(asUTC - offset);
}

function wallClockMinutes(timeStr) {
  if (!timeStr || !timeStr.includes(":")) return null;
  const pm = /pm/i.test(timeStr);
  const am = /am/i.test(timeStr);
  const [rawH, rawM] = timeStr.replace(/[^\d:]/g, "").split(":");
  let hours = Number(rawH);
  const minutes = Number(rawM || 0);
  if (!Number.isFinite(hours) || !Number.isFinite(minutes)) return null;
  if (pm && hours < 12) hours += 12;
  if (am && hours === 12) hours = 0;
  return hours * 60 + minutes;
}

function addCalendarDay(dateStr) {
  const [year, month, day] = String(dateStr || "").split("-").map(Number);
  if (!year || !month || !day) return dateStr;
  const nextDay = new Date(Date.UTC(year, month - 1, day + 1));
  return [
    nextDay.getUTCFullYear(),
    String(nextDay.getUTCMonth() + 1).padStart(2, "0"),
    String(nextDay.getUTCDate()).padStart(2, "0"),
  ].join("-");
}

// An end clock before the start clock represents the following day.
// For example, 11:00 PM to 12:00 AM is a one-hour event, not a negative span.
export function eventSpansMidnight(startTime, endTime) {
  const startMinutes = wallClockMinutes(startTime);
  const endMinutes = wallClockMinutes(endTime);
  return startMinutes !== null && endMinutes !== null && endMinutes < startMinutes;
}

export function getEventEndDate(event) {
  if (!event?.date || !event?.end_time) return null;
  const endDate = eventSpansMidnight(event.start_time || event.time, event.end_time)
    ? addCalendarDay(event.date)
    : event.date;
  return zonedWallClockToDate(endDate, event.end_time, event.timezone || "UTC");
}

export function getStandardRefundCutoff(event) {
  if (!event?.date) return null;
  const start = zonedWallClockToDate(
    event.date,
    event.start_time || event.time,
    event.timezone || "UTC"
  );
  if (!start) return null;
  const end = event.end_time
    ? getEventEndDate(event)
    : new Date(start.getTime() + (2 * 60 * 60 * 1000));
  if (!end || end.getTime() <= start.getTime()) return null;
  return new Date(start.getTime() + ((end.getTime() - start.getTime()) * 0.55));
}

export function isStandardRefundRequestAvailable(event, now = new Date()) {
  const cutoff = getStandardRefundCutoff(event);
  const current = now instanceof Date ? now : new Date(now);
  return Boolean(cutoff && !Number.isNaN(current.getTime()) && current.getTime() < cutoff.getTime());
}

// ShowMeLive's display-only date format. Event storage stays ISO (YYYY-MM-DD)
// so sorting, time-zone conversion, reminders, and payout scheduling are not
// affected by this presentation choice.
export function formatDateMMDDYear(value) {
  if (!value) return "";

  const raw = String(value);
  const wallClockMatch = raw.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (wallClockMatch) {
    return `${wallClockMatch[2]}-${wallClockMatch[3]}-${wallClockMatch[1]}`;
  }

  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return raw;
  return [
    String(date.getMonth() + 1).padStart(2, "0"),
    String(date.getDate()).padStart(2, "0"),
    String(date.getFullYear()),
  ].join("-");
}

// Format an event's schedule in the viewer's local timezone.
// Returns e.g. "07-14-2026 · 3:00 PM PDT – 3:15 PM PDT".
export function formatEventLocal(event) {
  if (!event) return "";
  const tz = event.timezone || "UTC";
  const start = zonedWallClockToDate(event.date, event.start_time || event.time, tz);

  if (!start) return event.date || "";

  const end = event.end_time ? getEventEndDate(event) : null;

  const dateStr = formatDateMMDDYear(start);

  const timeStr = new Intl.DateTimeFormat(undefined, {
    hour: "numeric", minute: "2-digit", timeZoneName: "short",
  }).format(start);

  if (end) {
    const endStr = new Intl.DateTimeFormat(undefined, {
      hour: "numeric", minute: "2-digit", timeZoneName: "short",
    }).format(end);
    return `${dateStr} · ${timeStr} – ${endStr}`;
  }
  return `${dateStr} · ${timeStr}`;
}
